import { Button, Spacer, Text } from '@loomhq/lens';
import React from 'react';
import { useDispatch } from 'react-redux';

import { showInviteTeammatesToolTip } from 'actions/layout';
import { openModal } from 'actions/modals';
import {
  INVITE_MEMBERS_NAV_BUTTON_CLICKED,
  INVITE_TEAMMATES_CLICKED,
  STORAGE_INCENTIVE_INVITE_TEAMMATES_CTA_CLICKED,
} from 'constants/events';
import { EXP_INVITE_STORAGE_INCENTIVES } from 'constants/featureFlag';
import { INVITE_CALLOUT_DISMISSED } from 'constants/localStorage';
import { TEAM_INVITE_MODAL } from 'constants/modalTypes';
import { WORKSPACE_PLAN_BUSINESS } from 'constants/workspacePlans';
import * as analytics from 'utilities/analytics';
import { createLocalWorkspaceStateHook } from 'utilities/local-workspace-state';

import inviteCLImagePng from 'img/request-to-join/invite-cl-teammates.png';
import inviteImagePng from 'img/request-to-join/invite-teammates.png';
import { useFlagIsActivated } from 'hooks/featureFlag';
import {
  useFetchSelectedWorkspaceBillingDetails,
  useGetSelectedWorkspace,
} from 'hooks/workspace';

import * as billingUtil from '../../../../utilities/billingAndPayments/billingDetailsUtil';
import SidebarCtaContainer from '../sidebar-cta-container';
import { FadeCallout } from '../fade-callout';
import { APP_SOURCE_MAP } from '../../../../../../shared/constants/analytics';
import { ANY } from '../../../../../../shared/constants/analyticsInviteTypes';
import { SIDEBAR } from '../../../../../../shared/constants/analyticsSources';
import { INVITE_STEP_COMPLETE } from '../../../../../../shared/constants/events';

import { INVITE_TEAMMATES_SIDEBAR_CALLOUT } from 'components/storage-incentives/constants';

export const useInviteCalloutDismissed = createLocalWorkspaceStateHook(
  INVITE_CALLOUT_DISMISSED,
  false
);

export default function InviteCallout({ displayMode = 'LARGE' }) {
  const dispatch = useDispatch();
  const isCollapsed = displayMode === 'COLLAPSED';
  const isCompact = displayMode === 'COMPACT';
  const [, setDismissed] = useInviteCalloutDismissed();

  const onDismiss = () => {
    dispatch(showInviteTeammatesToolTip(true));
    setDismissed(true);
  };

  const workspace = useGetSelectedWorkspace();
  const billingDetails = useFetchSelectedWorkspaceBillingDetails();
  const isTrialing = billingUtil.isTrialing(billingDetails);

  const showCreatorLiteCallout =
    workspace?.type === WORKSPACE_PLAN_BUSINESS && !isTrialing;

  const storageIncentives = useFlagIsActivated({
    flag: EXP_INVITE_STORAGE_INCENTIVES,
    activationValues: ['variant'],
  });

  const calloutText = storageIncentives
    ? 'New! Invite your team and get bonus video storage.'
    : showCreatorLiteCallout
    ? 'New! Invite members as Creator Lite for free.'
    : 'Invite your team to share and collaborate easily';

  return (
    <FadeCallout visible={!isCollapsed} width="expanded">
      <SidebarCtaContainer
        onDismiss={onDismiss}
        width="var(--navSidebarInnerWidthExpanded)"
      >
        <img
          className="self:center"
          src={showCreatorLiteCallout ? inviteCLImagePng : inviteImagePng}
          width="84"
          height="84"
          alt="Two people conversing happily, with colorful frills and a translucent geometric shape in the background"
          hidden={isCompact}
        />
        <Spacer top="small" bottom="medium">
          <Text
            alignment={isCompact ? 'left' : 'center'}
            fontWeight="bold"
            color="grey7"
          >
            {calloutText}
          </Text>
        </Spacer>
        <Button
          size="small"
          hasFullWidth
          disabled={false}
          onClick={() => {
            dispatch(openModal(TEAM_INVITE_MODAL));

            if (storageIncentives) {
              analytics.track(STORAGE_INCENTIVE_INVITE_TEAMMATES_CTA_CLICKED, {
                source: INVITE_TEAMMATES_SIDEBAR_CALLOUT,
                path: window.location.pathname,
              });
            } else {
              analytics.track(INVITE_MEMBERS_NAV_BUTTON_CLICKED, {
                path: window.location.pathname,
              });

              analytics.track(INVITE_STEP_COMPLETE, {
                step: INVITE_TEAMMATES_CLICKED,
                device: APP_SOURCE_MAP.WEB,
                source: SIDEBAR,
                inviteType: ANY,
                path: window.location.pathname,
              });
            }
          }}
          isCompact={isCollapsed}
          variant="primary"
        >
          Invite teammates
        </Button>
      </SidebarCtaContainer>
    </FadeCallout>
  );
}
